import type { FC, PropsWithChildren } from 'react';
import { createContext, useState, useMemo, useContext, useTransition, useEffect, useCallback } from 'react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import type { ThemeId } from '@/js/libs/ui/src/V2/Palette';
import { palette } from '@/js/libs/ui/src/V2/Palette';

import useLocalStorage, { StorageKey } from 'src/hooks/useLocalStorage';
import { PREFERS_DARK_QUERY, parseThemeSetting, resolveThemeId, applyThemeToDom, checkPrefersDark } from './helpers';
import type { ThemeSetting } from './helpers';

type Ctx = {
  themeId: ThemeId;
  isSystem: boolean;
  isSwitching: boolean;
  checkTheme: (id: ThemeSetting) => boolean;
  setTheme: (id: ThemeSetting) => void;
};

const ThemeControllerContext = createContext<Ctx | null>(null);

export const ThemeControllerProvider: FC<PropsWithChildren> = ({ children }) => {
  const { loadString, saveString } = useLocalStorage();

  // TODO: extend logic with useIdentityUser once the backend is ready
  const savedThemeSetting = useMemo(() => {
    const raw = loadString(StorageKey.THEME_ID);
    return parseThemeSetting(raw);
  }, [loadString]);

  const initialThemeId = useMemo<ThemeId>(() => {
    return resolveThemeId(savedThemeSetting);
  }, [savedThemeSetting]);

  const [themeId, setThemeIdState] = useState<ThemeId>(initialThemeId);
  const [isSystem, setSystem] = useState<boolean>(savedThemeSetting === 'system');
  const [isPending, startTransition] = useTransition();

  const setTheme = useCallbackRef((id: ThemeSetting) => {
    startTransition(() => {
      if (id === 'system') {
        setThemeIdState(checkPrefersDark() ? 'primaryDark' : 'primary');
        setSystem(true);
      } else {
        setThemeIdState(id);
        setSystem(false);
      }
      saveString(StorageKey.THEME_ID, id);
    });
  });

  // eslint-disable-next-line no-restricted-syntax
  const checkTheme = useCallback((id: ThemeSetting) => (id === 'system' ? isSystem : !isSystem && themeId === id), [themeId, isSystem]);

  useEffect(() => {
    if (!isSystem || typeof window === 'undefined' || !window.matchMedia) return () => {};

    const mql = window.matchMedia(PREFERS_DARK_QUERY);
    const onChange = (e: MediaQueryListEvent) => {
      setThemeIdState(e.matches ? 'primaryDark' : 'primary');
    };

    const currentPrefersDark = mql.matches;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setThemeIdState(currentPrefersDark ? 'primaryDark' : 'primary');

    mql.addEventListener('change', onChange);
    return () => {
      mql.removeEventListener('change', onChange);
    };
  }, [isSystem]);

  useEffect(() => {
    const bg = palette[themeId].colors.grey05_1 ?? palette.primary.colors.grey05_1;
    applyThemeToDom(themeId, bg);
  }, [themeId]);

  const value = useMemo(
    () => ({ themeId, isSystem, isSwitching: isPending, checkTheme, setTheme }),
    [themeId, isSystem, isPending, checkTheme, setTheme],
  );
  return <ThemeControllerContext.Provider value={value}>{children}</ThemeControllerContext.Provider>;
};

export const useThemeController = () => {
  const ctx = useContext(ThemeControllerContext);
  if (!ctx) throw new Error('useThemeController must be used within ThemeControllerProvider');
  return ctx;
};
